import { OrbitControls } from "@react-three/drei";
import { Canvas, useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import {
  axisVec,
  cubeletInLayer,
  moveAngleRad,
  originalPositionFromId,
  type Cubelet,
  type CubeState,
  type Move,
} from "../lib/cube";

interface AnimationState {
  move: Move;
  progress: number; // 0..1
}

interface Props {
  state: CubeState;
  animation: AnimationState | null;
}

const COLOR = {
  R: "#dc1f1f", // red    — right
  L: "#ff8c00", // orange — left
  U: "#ffffff", // white  — up
  D: "#ffd500", // yellow — down
  F: "#15994c", // green  — front
  B: "#173bcf", // blue   — back
  inside: "#0a0a0a",
};

const CUBELET_SIZE = 0.95;

function cubeletFaceColors(origPos: [number, number, number]): string[] {
  // BoxGeometry face order: +X, -X, +Y, -Y, +Z, -Z
  const [x, y, z] = origPos;
  return [
    x === 1 ? COLOR.R : COLOR.inside,
    x === -1 ? COLOR.L : COLOR.inside,
    y === 1 ? COLOR.U : COLOR.inside,
    y === -1 ? COLOR.D : COLOR.inside,
    z === 1 ? COLOR.F : COLOR.inside,
    z === -1 ? COLOR.B : COLOR.inside,
  ];
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export default function Cube3D({ state, animation }: Props) {
  return (
    <Canvas
      dpr={[1, 2]}
      camera={{ position: [4.5, 4.0, 5.5], fov: 38 }}
      shadows
    >
      <ambientLight intensity={0.85} />
      <directionalLight position={[6, 10, 6]} intensity={1.05} castShadow />
      <directionalLight position={[-5, -3, -6]} intensity={0.35} />
      <Scene state={state} animation={animation} />
      <OrbitControls
        enableDamping
        dampingFactor={0.08}
        rotateSpeed={0.7}
        zoomSpeed={0.6}
        minDistance={5}
        maxDistance={14}
        enablePan={false}
        touches={{ ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN }}
      />
    </Canvas>
  );
}

function Scene({ state, animation }: Props) {
  const layerGroupRef = useRef<THREE.Group>(null);

  // Compute the per-frame layer-rotation quaternion and apply to the group.
  useFrame(() => {
    if (!layerGroupRef.current || !animation) return;
    const targetAngle = moveAngleRad(animation.move);
    const eased = easeInOutCubic(animation.progress);
    const angle = eased * targetAngle;
    const axis = axisVec(animationAxis(animation.move));
    layerGroupRef.current.quaternion.setFromAxisAngle(axis, angle);
  });

  const inLayer = animation
    ? state.cubelets.filter((c) => cubeletInLayer(c, animation.move))
    : [];
  const stillCubelets = animation
    ? state.cubelets.filter((c) => !cubeletInLayer(c, animation.move))
    : state.cubelets;

  return (
    <group>
      {stillCubelets.map((c) => (
        <CubeletMesh key={c.id} cubelet={c} />
      ))}
      <group ref={layerGroupRef}>
        {inLayer.map((c) => (
          <CubeletMesh key={c.id} cubelet={c} />
        ))}
      </group>
    </group>
  );
}

function animationAxis(m: Move): "x" | "y" | "z" {
  if (m.face === "R" || m.face === "L") return "x";
  if (m.face === "U" || m.face === "D") return "y";
  return "z";
}

function CubeletMesh({ cubelet }: { cubelet: Cubelet }) {
  const origPos = useMemo(() => originalPositionFromId(cubelet.id), [cubelet.id]);
  const colors = useMemo(() => cubeletFaceColors(origPos), [origPos]);
  const materials = useMemo(
    () =>
      colors.map(
        (c) =>
          new THREE.MeshStandardMaterial({
            color: c,
            roughness: 0.42,
            metalness: 0.05,
          }),
      ),
    [colors],
  );
  // Cleanup materials when unmounting
  useEffect(() => () => materials.forEach((m) => m.dispose()), [materials]);

  // Apply current logical position + quaternion
  return (
    <mesh
      position={cubelet.position}
      quaternion={cubelet.quaternion}
      material={materials}
      castShadow
      receiveShadow
    >
      <boxGeometry args={[CUBELET_SIZE, CUBELET_SIZE, CUBELET_SIZE]} />
      <Edges color="#000" />
    </mesh>
  );
}

/** Black wireframe outline around each cubelet — gives the iconic Rubik's-cube look. */
function Edges({ color }: { color: string }) {
  // Use BufferGeometry + EdgesGeometry to draw only the cube edges
  const geo = useMemo(
    () => new THREE.EdgesGeometry(new THREE.BoxGeometry(CUBELET_SIZE, CUBELET_SIZE, CUBELET_SIZE)),
    [],
  );
  return (
    <lineSegments geometry={geo}>
      <lineBasicMaterial color={color} linewidth={2} />
    </lineSegments>
  );
}
